import React, { useRef, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ButtonGroup from '@mui/material/ButtonGroup'
import useMediaQuery from '@mui/material/useMediaQuery'
import useTheme from '@mui/material/styles/useTheme'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { useSwipeable } from 'react-swipeable'
import { useResize } from '@sales-center/components'

import { ProgressWidgetProps, ProgressItem, SegmentVariant } from './typings'
import { ProgressSegment } from './ProgressSegment'
import { useScrollNavigation } from './useScrollNavigation'

// ==================== Constants ====================

const MOBILE_SEGMENT_MIN_WIDTH_DEFAULT = 180
const MOBILE_HEIGHT_DEFAULT = 28

// ==================== Styles ====================

const PAGE_BUTTON_SX = {
  border: 'solid 1px #9a9a9a',
  minWidth: 33,
  height: 32,
  padding: 0,
  '&:hover': {
    border: 'solid 1px #6a6a6a',
  },
} as const

const PAGE_BUTTON_ICON_SX = {
  color: '#979797',
  width: 12,
  height: 12,
} as const

const LIST_SX = {
  display: 'inline-flex',
  flexDirection: 'row',
  padding: 0,
} as const

// ==================== Fade overlay styles ====================

const FADE_BASE_SX = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 20,
  pointerEvents: 'none',
  zIndex: 1,
  transition: 'opacity 0.3s',
} as const

const FADE_LEFT_SX = {
  ...FADE_BASE_SX,
  left: 0,
  background:
    'linear-gradient(to right, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
} as const

const FADE_RIGHT_SX = {
  ...FADE_BASE_SX,
  right: 0,
  background:
    'linear-gradient(to left, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
} as const

// ==================== Helper ====================

function getSegmentVariant(index: number, total: number): SegmentVariant {
  if (total === 1) return 'single'
  if (index === 0) return 'left'
  if (index === total - 1) return 'right'
  return 'middle'
}

// ==================== Component ====================

/**
 * ProgressWidget - A horizontal progress/stage indicator with chevron-shaped segments.
 *
 * Mobile responsive behavior:
 * - Detects viewport < `sm` breakpoint via MUI `useMediaQuery`
 * - Hides pagination buttons on mobile (uses swipe + :active touch feedback instead)
 * - Reduces segment height, min-width, font size, and padding automatically
 * - Adds edge-fade overlays to indicate scrollable overflow direction
 * - Supports `mobileSegmentMinWidth` and `mobileHeight` prop overrides
 */
export const ProgressWidget: React.FC<ProgressWidgetProps> = (props) => {
  const {
    scrollTo = 0,
    segmentMinWidth = 100,
    items,
    snap = true,
    opacity = 1,
    activeSegmentIndex,
    onActiveIndexChange,
    height: desktopHeight,
    colors,
    transitionTime,
    disabled,
    onChange,
    segmentSpacing,
    mobileSegmentMinWidth = MOBILE_SEGMENT_MIN_WIDTH_DEFAULT,
    mobileHeight = MOBILE_HEIGHT_DEFAULT,
  } = props

  // ========== Responsive detection ==========
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const effectiveHeight = isMobile ? mobileHeight : desktopHeight
  const effectiveMinWidth = isMobile ? mobileSegmentMinWidth : segmentMinWidth

  const containerRef = useRef<HTMLDivElement>(null)
  const { width: containerWidth, marginToNextElement } = useResize(containerRef)

  const {
    scrollAmount,
    showPageButtons,
    segmentWidth,
    marginLeft,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
  } = useScrollNavigation({
    containerWidth,
    itemCount: items.length,
    segmentMinWidth: effectiveMinWidth,
    segmentSpacing,
    height: effectiveHeight,
    marginToNextElement,
    scrollTo,
    snap,
    hidePageButtons: isMobile,
  })

  // Close callback for custom click handlers
  const handleCustomClose = useCallback(() => {
    onActiveIndexChange(null)
  }, [onActiveIndexChange])

  // Segment click handler
  const handleSegmentClick = useCallback(
    (
      event: React.MouseEvent<HTMLLIElement, MouseEvent>,
      item: ProgressItem,
      index: number,
    ) => {
      if (item.onClick) {
        item.onClick(event, handleCustomClose)
        onActiveIndexChange(index)
      } else {
        onChange(item.value)
        onActiveIndexChange(null)
      }
    },
    [handleCustomClose, onActiveIndexChange, onChange],
  )

  // Swipe support for touch devices
  const swipeHandlers = useSwipeable({
    onSwipedLeft: (eventData) => {
      eventData.event.preventDefault()
      handleScrollRight()
    },
    onSwipedRight: (eventData) => {
      eventData.event.preventDefault()
      handleScrollLeft()
    },
    trackTouch: true,
    trackMouse: false,
  })

  // Memoize the segment list to avoid recreating on every render
  const segmentElements = useMemo(
    () =>
      items.map((item, index) => {
        const isComplete = item.isComplete
        const segmentColors = isComplete
          ? colors.completeColors
          : colors.incompleteColors

        return (
          <ProgressSegment
            key={item.value ?? `segment-${index}`}
            color={segmentColors.backgroundColor}
            colorHover={segmentColors.backgroundColorHover}
            disabled={disabled}
            endAdornment={item.endAdornment}
            height={effectiveHeight}
            isMobile={isMobile}
            isOpen={activeSegmentIndex === index}
            marginLeft={index === 0 ? 0 : marginLeft}
            onClick={(event) => handleSegmentClick(event, item, index)}
            padding={index === 0 ? '0 12px' : '0 34px'}
            text={item.text}
            textColor={segmentColors.textColor}
            textColorHover={segmentColors.textColorHover}
            transitionTime={transitionTime}
            value={item.value}
            variant={getSegmentVariant(index, items.length)}
            width={segmentWidth}
            dataTestId={item.dataTestId}
          />
        )
      }),
    [
      items,
      colors,
      disabled,
      effectiveHeight,
      isMobile,
      activeSegmentIndex,
      marginLeft,
      handleSegmentClick,
      transitionTime,
      segmentWidth,
    ],
  )

  // Whether we need fade overlays (content overflows)
  const showFades = isMobile && (canScrollLeft || canScrollRight)

  return (
    <Grid container ref={containerRef} wrap="nowrap">
      {containerWidth ? (
        <>
          {/* Scrollable segment area */}
          <Grid
            item
            sx={{
              pointerEvents: disabled ? 'none' : 'auto',
              opacity: disabled ? 0.5 : opacity,
              overflow: 'hidden',
              width: '100%',
              position: 'relative',
              transition: 'opacity 0.5s',
              // Better touch handling on mobile
              ...(isMobile && {
                touchAction: 'pan-y',
                WebkitOverflowScrolling: 'touch',
              }),
            }}
            {...swipeHandlers}
          >
            {/* Edge fade overlays to hint at scrollable content */}
            {showFades && (
              <>
                <Box
                  sx={{
                    ...FADE_LEFT_SX,
                    opacity: canScrollLeft ? 1 : 0,
                  }}
                />
                <Box
                  sx={{
                    ...FADE_RIGHT_SX,
                    opacity: canScrollRight ? 1 : 0,
                  }}
                />
              </>
            )}

            <List
              sx={LIST_SX}
              dense
              disablePadding
              style={{
                position: 'relative',
                transition: 'left 0.25s ease-out',
                left: scrollAmount,
                top: 0,
              }}
            >
              {segmentElements}
            </List>
          </Grid>

          {/* Pagination Buttons — hidden on mobile (swipe is used instead) */}
          {showPageButtons && (
            <Grid item width="80px" textAlign="right" marginLeft="20px">
              <ButtonGroup
                color="primary"
                disabled={disabled}
                aria-label="Progress scroll buttons"
                size="small"
              >
                <Button
                  aria-label="Scroll left"
                  sx={PAGE_BUTTON_SX}
                  color="primary"
                  disabled={!canScrollLeft}
                  onClick={handleScrollLeft}
                  data-test-id="opportunityCardProgressScrollLeftButton"
                >
                  <ArrowBackIcon sx={PAGE_BUTTON_ICON_SX} />
                </Button>
                <Button
                  aria-label="Scroll right"
                  sx={PAGE_BUTTON_SX}
                  color="primary"
                  disabled={!canScrollRight}
                  onClick={handleScrollRight}
                  data-test-id="opportunityCardProgressScrollRightButton"
                >
                  <ArrowForwardIcon sx={PAGE_BUTTON_ICON_SX} />
                </Button>
              </ButtonGroup>
            </Grid>
          )}
        </>
      ) : null}
    </Grid>
  )
}

ProgressWidget.displayName = 'ProgressWidget'
